import useWindowSize from "@/hooks/useWindowSize";
import {
  CreatorCategoryEnum,
  SuccessStoryType,
} from "@/shared/types/common.types";
import { isBrowser } from "@/shared/utils/dom";
// import { faCaretDown, faCaretUp } from "@fortawesome/pro-solid-svg-icons";
// import { Icon } from "@graphy/ui";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SuccessStoryGridCard from "./SuccessStoryGridCard";
// import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
// import Amplitude from "@/shared/services/amplitude.service";
// import {
//   getCurrentURL,
//   getReferrer,
//   getScreenWidth,
// } from "@/shared/utils/commons";
// import { getFromLocal } from "@/shared/utils/storage.util";
import { useNavStore } from "@/shared/store/useNavStore";
import tailwindConfig from "@/tailwind.config";
import resolveConfig from "tailwindcss/resolveConfig";

const fullConfig = resolveConfig(tailwindConfig);

export type SuccessStoriesGridPropType = {
  successStories: SuccessStoryType[];
};

const SuccessStoriesGrid = ({ successStories }: SuccessStoriesGridPropType) => {
  const heroContainerRef = useRef<HTMLDivElement>(null);
  const col1Ref = useRef<HTMLDivElement>(null);
  const col2Ref = useRef<HTMLDivElement>(null);
  const col3Ref = useRef<HTMLDivElement>(null);
  // const [isOpen, setIsOpen] = useState(!true);
  const [selectedCategory, setSelectedCategory] = useState<CreatorCategoryEnum>(
    CreatorCategoryEnum.AllCategories
  );

  const { windowWidth } = useWindowSize();

  const { setShowSignUp } = useNavStore();

  // const toggleCategorySelector = () => {
  //   const { current_url, screen_width, referrer, utm_params } =
  //     LANDING_PAGE_EVENTS.ALL_CREATOR_CATEGORIES_TAPPED.PROPERTIES;
  //   new Amplitude().sendEvent({
  //     type: LANDING_PAGE_EVENTS.ALL_CREATOR_CATEGORIES_TAPPED.LABEL,
  //     properties: {
  //       [current_url]: getCurrentURL(),
  //       [screen_width]: getScreenWidth(),
  //       [referrer]: getReferrer(),
  //       [utm_params]: getFromLocal("utmFields"),
  //     } as { [key: string]: string },
  //   });
  //   setIsOpen(!isOpen);
  // };

  // const selectCategory = (categoryValue: CreatorCategoryEnum) => {
  //   const { current_url, screen_width, referrer, utm_params, category } =
  //     LANDING_PAGE_EVENTS.CREATOR_CATERGORY_SELECTED.PROPERTIES;
  //   new Amplitude().sendEvent({
  //     type: LANDING_PAGE_EVENTS.CREATOR_CATERGORY_SELECTED.LABEL,
  //     properties: {
  //       [current_url]: getCurrentURL(),
  //       [screen_width]: getScreenWidth(),
  //       [referrer]: getReferrer(),
  //       [utm_params]: getFromLocal("utmFields"),
  //       [category]: categoryValue,
  //     } as { [key: string]: string },
  //   });
  //   setSelectedCategory(categoryValue);
  //   toggleCategorySelector();
  //   // TODO: Maybe scroll to the top whenever changing the category.
  // };

  const numberOfCols = useMemo(() => {
    if (!windowWidth) return 0;

    const screens = fullConfig.theme?.screens as {
      sm: string;
      md: string;
      lg: string;
    };
    if (screens?.lg && +screens?.lg?.replace("px", "") <= windowWidth) {
      return 3;
    }
    if (screens?.sm && +screens?.sm?.replace("px", "") <= windowWidth) {
      return 2;
    }
    return 1;
  }, [windowWidth]);

  const colStories = useMemo(() => {
    let colStories: {
      1: SuccessStoryType[];
      2: SuccessStoryType[];
      3: SuccessStoryType[];
    } = {
      1: [],
      2: [],
      3: [],
    };

    if (numberOfCols === 0) {
      return colStories;
    }

    let filteredSuccessStories = successStories;
    if (selectedCategory !== CreatorCategoryEnum.AllCategories) {
      filteredSuccessStories = successStories.filter(
        (successStory) => successStory.category === selectedCategory
      );
    }
    filteredSuccessStories.forEach((succesStory, index) => {
      let col = ((index % numberOfCols) + 1) as 1 | 2 | 3;

      colStories[col].push(succesStory);
    });

    return colStories;
  }, [numberOfCols, selectedCategory, successStories]);

  const onScrollRAF = useCallback(() => {
    if (heroContainerRef.current) {
      const scrollPosition = window.scrollY;
      let { top, height } = heroContainerRef.current.getBoundingClientRect();

      let scrollPercentage =
        ((scrollPosition - heroContainerRef.current.clientTop) /
          (height - window.innerHeight * 0.5)) *
        100;

      scrollPercentage = Math.min(Math.max(0, scrollPercentage), 100);

      if (0 <= scrollPercentage && scrollPercentage <= 100) {
        if (numberOfCols >= 2 && col1Ref.current && col2Ref.current) {
          let { height: col1Height } = col1Ref.current.getBoundingClientRect();
          const translateYCol1Range = height - col1Height;

          col1Ref.current.style.transform = `translateY(${
            (translateYCol1Range * scrollPercentage) / 100
          }px)`;

          let { height: col2Height } = col2Ref.current.getBoundingClientRect();
          const translateYCol2Range = height - col2Height;

          col2Ref.current.style.transform = `translateY(${
            (translateYCol2Range * scrollPercentage) / 100
          }px)`;
        }
        if (numberOfCols === 3 && col3Ref.current) {
          let { height: col3Height } = col3Ref.current.getBoundingClientRect();

          const translateYCol3Range = height - col3Height;

          col3Ref.current.style.transform = `translateY(${
            (translateYCol3Range * scrollPercentage) / 100
          }px)`;
        }
      }
    }
  }, [numberOfCols]);

  const handleWindowScroll = useCallback(() => {
    requestAnimationFrame(onScrollRAF);
  }, [onScrollRAF]);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (!scrollY) {
        setShowSignUp(true);
      } else {
        setShowSignUp(false);
      }
    });
    return () => {
      setShowSignUp(true);
    };
  }, []);

  useEffect(() => {
    if (isBrowser) {
      window.addEventListener("scroll", handleWindowScroll)};
    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, [handleWindowScroll]);

  if (numberOfCols === 0) {
    return <div className="h-screen w-screen"></div>;
  }

  return (
    <>
      <div
        className="~bg-green-0 relative box-border grid min-h-[550px] gap-8 sm:grid-cols-2 lg:grid-cols-3"
        ref={heroContainerRef}
      >
        <div className="~bg-purple-0 box-border">
          <div
            className={classNames(
              "box-border flex flex-col gap-8 transition-transform duration-0",
              {
                "pt-[400px]": numberOfCols >= 2,
              }
            )}
            ref={col1Ref}
          >
            {colStories["1"].map((successStory, index) => (
              <SuccessStoryGridCard
                key={index}
                index={index}
                successStory={successStory}
              />
            ))}
          </div>
        </div>
        {numberOfCols >= 2 && colStories["2"]?.length > 0 && (
          <div className="~bg-purple-0 box-border">
            <div
              className="box-border flex flex-col gap-8 pt-[200px] transition-transform duration-0"
              ref={col2Ref}
            >
              {colStories["2"].map((successStory, index) => (
                <SuccessStoryGridCard
                  key={index}
                  index={index}
                  successStory={successStory}
                />
              ))}
            </div>
          </div>
        )}
        {numberOfCols >= 3 && colStories["3"]?.length > 0 && (
          <div className="~bg-purple-0 box-border">
            <div
              className="box-border flex flex-col gap-8 transition-transform duration-0"
              ref={col3Ref}
            >
              {colStories["3"].map((successStory, index) => (
                <SuccessStoryGridCard
                  key={index}
                  index={index}
                  successStory={successStory}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {/* <div
        className={classNames(
          "sticky bottom-6 left-1/2 mx-auto my-6 inline-flex min-w-[154px] -translate-x-1/2 cursor-pointer items-center justify-center gap-2 bg-cta-primary py-3 pl-6 pr-4 text-body font-bold text-grey-0 transition-all duration-300",
          {
            "rounded-b-xl": isOpen,
            "rounded-b-xl rounded-t-xl": !isOpen,
          }
        )}
        onClick={toggleCategorySelector}
      >
        <div className="text-title-14 transition-all duration-300">
          {selectedCategory}
        </div>
        <Icon icon={isOpen ? faCaretDown : faCaretUp} />
        {isOpen && (
          <div className="absolute bottom-full left-0 flex w-full flex-col gap-2 rounded-t-xl border border-grey-2 bg-grey-0 py-2">
            {Object.entries(CreatorCategoryEnum).map(
              ([categoryKey, categoryValue]) => (
                <div
                  key={categoryKey}
                  className={classNames(
                    "cursor-pointer px-3 py-2.5 text-center text-caption font-semibold text-grey-4 hover:bg-grey-1",
                    {
                      "text-purple-3": selectedCategory === categoryValue,
                    }
                  )}
                  onClick={() => selectCategory(categoryValue)}
                >
                  {categoryValue}
                </div>
              )
            )}
          </div>
        )}
      </div> */}
    </>
  );
};

export default SuccessStoriesGrid;
